import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRACKERTYPE_LOAD,
    ACTION_TRACKERTYPE_LOAD_BY_ID,
    ACTION_TRACKERTYPE_LOAD_FAILURE,
    ACTION_TRACKERTYPE_LOAD_SUCCESS,

    ACTION_TRACKERTYPE_SAVE,
    ACTION_TRACKERTYPE_SAVE_SUCCESS,
    ACTION_TRACKERTYPE_SAVE_FAILURE,

    ACTION_TRACKERTYPE_CREATE,
    ACTION_TRACKERTYPE_CREATE_SUCCESS,
    ACTION_TRACKERTYPE_CREATE_FAILURE,

    ACTION_TRACKERTYPE_VALIDATE,
    ACTION_TRACKERTYPE_VALIDATE_SUCCESS,
    ACTION_TRACKERTYPE_VALIDATE_FAILURE,

    ACTION_TRACKERTYPE_CLEAR,
    ACTION_TRACKERTYPE_FILTER,

    TrackerTypeAction
} from '../actions/trackerTypes';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrackerTypeState {
    formData: Dtos.TrackerType | Dtos.TrackerType[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrackerType[];
}

const initialState: ITrackerTypeState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: ITrackerTypeState, _isLoading: boolean): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ITrackerTypeState,
    formData: Dtos.TrackerType | Dtos.TrackerType[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: ITrackerTypeState, formFilter: IFormFilter): ITrackerTypeState {

    const interests: Dtos.TrackerType[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = interests ? interests.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ITrackerTypeState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: ITrackerTypeState, responseStatus: Dtos.ResponseStatus): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: ITrackerTypeState, propertiesToClear?: string[]): ITrackerTypeState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: ITrackerTypeState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrackerTypeState, _isSaving: boolean): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrackerTypeState,
    formData: Dtos.TrackerType | Dtos.TrackerType[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrackerTypeState, responseStatus: Dtos.ResponseStatus): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrackerTypeState, _isCreating: boolean): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrackerTypeState,
    formData: Dtos.TrackerType,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrackerTypeState, responseStatus: Dtos.ResponseStatus): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ITrackerTypeState, _isValidating: boolean): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrackerTypeState,
    formData: Dtos.TrackerType,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrackerTypeState, responseStatus: Dtos.ResponseStatus): ITrackerTypeState {
    const newState: ITrackerTypeState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const TrackerTypeReducer: Reducer<ITrackerTypeState> = (state: ITrackerTypeState = initialState, action: TrackerTypeAction) => {
    switch (action.type) {
        case ACTION_TRACKERTYPE_LOAD:
        case ACTION_TRACKERTYPE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRACKERTYPE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRACKERTYPE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRACKERTYPE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRACKERTYPE_SAVE:
            return saving(state, true);
        case ACTION_TRACKERTYPE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRACKERTYPE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRACKERTYPE_CREATE:
            return creating(state, true);
        case ACTION_TRACKERTYPE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRACKERTYPE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRACKERTYPE_VALIDATE:
            return validating(state, true);
        case ACTION_TRACKERTYPE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRACKERTYPE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRACKERTYPE_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default TrackerTypeReducer;